import React, { KeyboardEvent, useRef } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { useEnquiry } from '../../contexts/EnquiryContext';
import { industries } from '../../data/industries';
import { Button } from '../ui/Button';
import { Reveal } from '../ui/Reveal';
import { SectionHeader } from '../ui/SectionHeader';
export function Industries() {
  const {
    activeIndustry,
    setActiveIndustry,
    startEnquiry,
    setUseCase
  } = useEnquiry();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeIndex = industries.findIndex(industry => industry.id === activeIndustry);
  const active = industries[activeIndex];
  const choose = (index: number) => {
    const next = industries[index];
    setActiveIndustry(next.id);
    setUseCase(next.id);
    tabRefs.current[index]?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      choose((activeIndex + 1) % industries.length);
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      choose((activeIndex - 1 + industries.length) % industries.length);
    }
  };
  return <section id="industries" aria-labelledby="industries-title" className="bg-white" data-id="element-359" data-mp="koiz7ahyu8uj">
      <div className="mx-auto max-w-page px-5 py-20 lg:px-8 lg:py-28" data-id="element-360" data-mp="gmp37fiptt0p">
        <Reveal data-id="element-361" data-mp="gkb856o1cgt5">
          <SectionHeader id="industries-title" title="Built around the site you’re powering" intro="Choose your sector to see the problems we solve there and where to start." data-id="element-362" data-mp="3p28tc5o3avbj" />
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-[300px_1fr] lg:gap-12" data-id="element-363" data-mp="1q6ohkbeoqwu">
          <div role="tablist" aria-label="Industries" aria-orientation="vertical" onKeyDown={onKeyDown} className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:px-0" data-id="element-364" data-mp="3t3as6le3h5cz">
            {industries.map((industry, index) => {
            const selected = industry.id === activeIndustry;
            return <button key={industry.id} ref={element => {
              tabRefs.current[index] = element;
            }} role="tab" id={`tab-${industry.id}`} aria-selected={selected} aria-controls={`panel-${industry.id}`} tabIndex={selected ? 0 : -1} onClick={() => choose(index)} className={`shrink-0 rounded-btn px-4 py-3 text-left text-[15px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-afb-turquoise lg:flex lg:items-center lg:justify-between ${selected ? 'bg-afb-ink text-white' : 'text-afb-ink/75 hover:bg-afb-light hover:text-afb-ink'}`} data-id="element-365" data-mp="199is2i98vvfc" data-mpi={index}>
                  {industry.label}
                  <ArrowRightIcon aria-hidden="true" className={`hidden h-4 w-4 lg:block ${selected ? 'text-afb-turquoise' : 'opacity-0'}`} data-id="element-366" data-mp="1769dgcgy1b" />
                </button>;
          })}
          </div>

          <div key={active.id} role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} className="panel-in rounded-card border border-afb-line bg-afb-light p-6 md:p-10" data-id="element-367" data-mp="3hj02mh9yf7jq">
            <p className="text-sm text-afb-muted" data-id="element-368" data-mp="38k4yrbudu9jl">{active.who}</p>
            <p className="mt-4 max-w-2xl font-sora text-2xl font-semibold leading-snug tracking-[-0.01em] text-afb-ink md:text-[1.75rem]" data-id="element-369" data-mp="33k0j1o1atiuf">
              {active.pressure}
            </p>

            <div className="mt-8 grid gap-8 md:grid-cols-2" data-id="element-370" data-mp="11rufklaizrrb">
              <div data-id="element-371" data-mp="wshsq0jnmkoq">
                <h3 className="text-sm font-semibold text-afb-ink" data-id="element-372" data-mp="wrzoagmu905q">How AFB fits</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-afb-ink/80" data-id="element-373" data-mp="3qkhf69ya0l23">{active.fit}</p>
              </div>
              <div data-id="element-374" data-mp="3umkuxarzabt6">
                <h3 className="text-sm font-semibold text-afb-ink" data-id="element-375" data-mp="3ul1enk6bo0re">Problems we solve here</h3>
                <ul className="mt-3 flex flex-wrap gap-2" data-id="element-376" data-mp="3gl8e6x4zu668">
                  {active.pressures.map(pressure => <li key={pressure} className="rounded-full border border-afb-line bg-white px-3 py-1.5 text-sm text-afb-ink/80" data-id="element-377" data-mp="3lk3an1vvvt8t">
                      {pressure}
                    </li>)}
                </ul>
                <a href="#process" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-afb-teal underline-offset-4 hover:underline" data-id="element-378" data-mp="3bp1irya84d2j">
                  See how it works
                  <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-379" data-mp="2nxcnc923v8hd" />
                </a>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-4 border-t border-afb-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-380" data-mp="2svxlm2xsri36">
              <p className="text-[15px] text-afb-ink/80" data-id="element-381" data-mp="259bvhtxlhvz3">
                Start with:{' '}
                <a href={active.startWith.href} className="font-semibold text-afb-ink underline underline-offset-4" data-id="element-382" data-mp="25boi4keasuih">
                  {active.startWith.label}
                </a>
              </p>
              <Button onClick={() => startEnquiry({
              useCase: active.id
            })} variant="ink" data-id="element-383" data-mp="2eccz9nyznazl">
                Discuss your site
                <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-384" data-mp="2o8prehevxgs8" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}